/**
 * Authentication Side Panel Component
 * Per design guidelines: Light blue gradient, workspace image, benefits
 * ONLY allowed gradient in entire auth system (max 40% viewport)
 */
import React from 'react';

export type AuthSidePanelVariant = 'login' | 'register';

interface SidePanelContent {
  image: string;
  title: string;
  subtitle: string;
  benefits: readonly string[];
}

const CONTENT: Record<AuthSidePanelVariant, SidePanelContent> = {
  login: {
    image: 'https://images.unsplash.com/photo-1696453423495-046a7d83bf55',
    title: 'Welcome Back to UserLife',
    subtitle: 'Streamline your workforce management',
    benefits: [
      'Automated employee lifecycle workflows',
      'Seamless Microsoft 365 integration',
      'Real-time audit trails and compliance',
    ],
  },
  register: {
    image: 'https://images.pexels.com/photos/4064835/pexels-photo-4064835.jpeg',
    title: 'Start Your Journey',
    subtitle: 'Transform how you manage your workforce',
    benefits: [
      '14-day free trial - no credit card required',
      'Set up in minutes, not hours',
      'Join 500+ companies automating HR processes',
    ],
  },
};

interface AuthSidePanelProps {
  variant?: AuthSidePanelVariant;
}

export const AuthSidePanel = ({ variant = 'login' }: AuthSidePanelProps) => {
  const { image, title, subtitle, benefits } = CONTENT[variant];

  return (
    <div
      className="hidden lg:flex lg:flex-col lg:justify-center lg:items-center relative overflow-hidden p-12"
      style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #dbeafe 100%)',
      }}
      data-testid="auth-side-panel"
    >
      {/* Background Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1605106702734-205df224ecce)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-md">
        {/* Logo */}
        <div className="mb-8">
          <h1
            className="text-4xl font-bold text-slate-800"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            UserLife JML
          </h1>
          <p className="text-slate-600 mt-2">Employee Lifecycle Management</p>
        </div>

        {/* Hero Image */}
        <img
          src={image}
          alt="Modern workspace"
          className="rounded-2xl shadow-2xl mb-8 w-full"
          data-testid="hero-image"
        />

        {/* Title & Subtitle */}
        <h2
          className="text-3xl font-bold text-slate-800 mb-4"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          {title}
        </h2>
        <p className="text-lg text-slate-600 mb-6">{subtitle}</p>

        {/* Benefits List */}
        <ul className="space-y-3">
          {benefits.map((benefit, index) => (
            <li key={index} className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-slate-700">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
